import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import { ConfirmModal } from '../../components/ConfirmModal';
import {
  acceptProBooking,
  approveReschedule,
  cancelProBooking,
  completeProBooking,
  declineProBooking,
  declineReschedule,
  getProBookingDetail,
  rejectProBooking,
  startProBooking,
} from '../../../lib/professional-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { downloadProPayoutInvoice } from '../../../lib/pro-payout-invoice';

// Actions that show a branded ConfirmModal instead of the native
// browser confirm(). Decline stays on `prompt()` for now because it
// needs free-text input that ConfirmModal doesn't support.
type ConfirmableAction =
  | 'accept'
  | 'reject'
  | 'cancel'
  | 'start'
  | 'complete'
  | 'approveReschedule'
  | 'declineReschedule';

const CONFIRM_COPY: Record<
  ConfirmableAction,
  { title: string; message: string; confirmLabel: string }
> = {
  accept: {
    title: 'Accept this booking?',
    message:
      "You're confirming you'll do this job at the scheduled time. The customer will be notified and their payment will be released to you on completion.",
    confirmLabel: 'Yes, accept',
  },
  reject: {
    title: 'Reject this booking?',
    message:
      'The customer will be fully refunded — wallet portion back to their wallet (instant), card portion back to their card (5–10 business days). This cannot be undone.',
    confirmLabel: 'Yes, reject',
  },
  cancel: {
    title: 'Cancel this booking?',
    message:
      "Pro cancellations always refund 100%. The wallet portion goes back to the customer's wallet (instant); the card portion is refunded via Stripe (5–10 business days). This cannot be undone.",
    confirmLabel: 'Yes, cancel',
  },
  start: {
    title: 'Mark this booking in progress?',
    message:
      "You're confirming you've arrived on-site and started the job. The customer will see the booking status flip to In Progress.",
    confirmLabel: 'Yes, start',
  },
  complete: {
    title: 'Complete this booking?',
    message:
      "You're confirming the job is finished. The customer is notified, a review request goes out, and your payout for this booking moves into the payout queue. This cannot be undone.",
    confirmLabel: 'Yes, complete',
  },
  approveReschedule: {
    title: 'Approve the new time?',
    message:
      "You're confirming you can do this job at the customer's requested new time. The booking moves to that time and the customer is notified.",
    confirmLabel: 'Yes, approve',
  },
  declineReschedule: {
    title: 'Decline the new time?',
    message:
      "The booking stays at its original time — nothing is cancelled or refunded. The customer is told you couldn't make the new time.",
    confirmLabel: 'Yes, decline',
  },
};

// Same status → label / styling mapping the list page uses so the
// two views agree visually.
const STATUS_LABEL: Record<string, string> = {
  PENDING_PROFESSIONAL_CONFIRM: 'Awaiting your confirmation',
  CONFIRMED: 'Booked',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  PENDING_PAYMENT: 'Awaiting payment',
  CANCELLED_BY_CUSTOMER: 'Cancelled — customer',
  CANCELLED_BY_PRO: 'Cancelled by you',
  CANCELLED_BY_ADMIN: 'Cancelled — admin',
  REFUNDED: 'Refunded',
  FAILED: 'Payment failed',
};

const STATUS_STYLES: Record<string, string> = {
  PENDING_PROFESSIONAL_CONFIRM:
    'bg-[#fff7e6] text-[#9a6700] border border-[#fde2a1]',
  CONFIRMED: 'bg-[#f5f0e6] text-[#8e2a2f]',
  IN_PROGRESS: 'bg-[#8e2a2f] text-white',
  COMPLETED: 'bg-[#5a5c55] text-white',
  PENDING_PAYMENT: 'bg-[#fcf3d6] text-[#8e2a2f]',
  CANCELLED_BY_CUSTOMER: 'bg-[#e8cd96] text-[#8e2a2f]',
  CANCELLED_BY_PRO: 'bg-[#e8cd96] text-[#8e2a2f]',
  CANCELLED_BY_ADMIN: 'bg-[#e8cd96] text-[#8e2a2f]',
  REFUNDED: 'bg-[#e8cd96] text-[#8e2a2f]',
  FAILED: 'bg-[#f5d6d6] text-[#8e2a2f]',
};

function fullName(first: string | null, last: string | null): string {
  return [first, last].filter(Boolean).join(' ') || '—';
}

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export default function BookingDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [info, setInfo] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  // Pending action awaiting modal confirmation. Decline still uses
  // prompt() because it needs a free-text reason; the other three
  // confirm via ConfirmModal so the pro sees branded copy that
  // explains the refund consequences.
  const [pendingConfirm, setPendingConfirm] = useState<ConfirmableAction | null>(
    null,
  );

  async function load() {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getProBookingDetail(id);
      setInfo(res.info);
    } catch (err: any) {
      toast.error(
        apiErrorMessage(err, 'Failed to load'),
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Click handler for the action buttons. Every action except
  // `decline` (which still needs a free-text reason via prompt())
  // routes through the ConfirmModal so the pro can't fat-finger an
  // irreversible step like Complete or Reject.
  function handleAction(
    action:
      | 'start'
      | 'complete'
      | 'cancel'
      | 'decline'
      | 'accept'
      | 'reject'
      | 'approveReschedule'
      | 'declineReschedule',
  ) {
    if (!info) return;
    if (
      action === 'accept' ||
      action === 'reject' ||
      action === 'cancel' ||
      action === 'start' ||
      action === 'complete' ||
      action === 'approveReschedule' ||
      action === 'declineReschedule'
    ) {
      setPendingConfirm(action);
      return;
    }
    // decline — ConfirmModal doesn't support a text input so we keep
    // the native prompt() for the reason. Could be promoted to a
    // dedicated DeclineReasonModal later if the UX feels jarring.
    const reason = prompt(
      "Why are you declining? The booking stays active for the customer and admin will reassign it to another pro.",
      '',
    );
    if (reason === null) return;
    if (!reason.trim()) {
      alert('Please give a short reason so admin can find a replacement.');
      return;
    }
    void runDecline(reason);
  }

  // Modal "Yes, …" handler. Runs the API call for whichever
  // confirmable action is currently pending, then closes the modal.
  async function runConfirmed() {
    if (!info || !pendingConfirm) return;
    setActing(true);
    try {
      switch (pendingConfirm) {
        case 'accept':
          await acceptProBooking(info.id);
          toast.success('Booking accepted.');
          break;
        case 'reject':
          await rejectProBooking(info.id, null);
          toast.success('Booking rejected. The customer will be refunded.');
          break;
        case 'cancel':
          await cancelProBooking(info.id);
          toast.success('Booking cancelled. The customer will be refunded.');
          break;
        case 'start':
          await startProBooking(info.id);
          toast.success('Booking marked in progress.');
          break;
        case 'complete':
          await completeProBooking(info.id);
          toast.success('Booking completed. Payout is on its way.');
          break;
        case 'approveReschedule':
          await approveReschedule(info.id);
          toast.success('New time approved. The customer has been notified.');
          break;
        case 'declineReschedule':
          await declineReschedule(info.id, null);
          toast.success('Reschedule declined. The booking stays as it was.');
          break;
      }
      const done = pendingConfirm;
      setPendingConfirm(null);
      // A pro cancel detaches them from the booking (it moves to the admin
      // reassignment queue, professional → null). Reloading the detail in
      // place leaves a stale "Cancel booking" button on a row they no longer
      // own — a second tap then throws "You don't have access to this
      // booking". Bounce to the list instead, exactly like decline. (Issue 1)
      if (done === 'cancel') {
        navigate('/professional/bookings');
        return;
      }
      await load();
    } catch (err: any) {
      const msg = apiErrorMessage(err, 'Action failed.');
      toast.error(msg);
      // If the booking changed underneath us — reassigned away, no longer
      // ours, already actioned, or expired by the stale-confirmation cron —
      // the detail on screen is stale and every retry will keep throwing
      // "You don't have access to this booking". Bounce to the list so the
      // pro isn't stranded on a dead page. (Issue 1 — Reject/Accept)
      if (/access|not part|no longer|not found|already|n't your/i.test(msg)) {
        navigate('/professional/bookings');
      }
    } finally {
      setActing(false);
    }
  }

  // Decline keeps its own runner because it carries a free-text
  // reason and navigates away from the page on success — the
  // ConfirmModal flow above always reloads in place.
  async function runDecline(reason: string) {
    if (!info) return;
    setActing(true);
    try {
      await declineProBooking(info.id, reason);
      // Decline pulls the booking off the pro — bounce back to the
      // list instead of trying to reload a row they no longer own.
      navigate('/professional/bookings');
      return;
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Action failed.'));
    } finally {
      setActing(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <HeaderPro />
        <main className="flex-1 max-w-[800px] mx-auto px-4 py-8 w-full">
          {/* Skeleton mirrors the real layout (header strip + 2×2
              card grid + action row) so the page doesn't jump when
              data arrives. */}
          <div className="animate-pulse space-y-6">
            <div className="h-4 w-32 bg-[#f0eded] rounded" />
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2 flex-1">
                <div className="h-6 w-1/2 bg-[#f0eded] rounded" />
                <div className="h-4 w-1/3 bg-[#f5f1f2] rounded" />
              </div>
              <div className="h-7 w-24 bg-[#f5f1f2] rounded-full" />
            </div>
            <div className="flex gap-2">
              <div className="h-9 w-32 bg-[#f0eded] rounded-lg" />
              <div className="h-9 w-32 bg-[#f5f1f2] rounded-lg" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="border border-[#ede8e9] rounded-[10px] bg-white p-5 space-y-3"
                >
                  <div className="h-3 w-1/3 bg-[#f0eded] rounded" />
                  <div className="h-4 w-3/4 bg-[#f5f1f2] rounded" />
                  <div className="h-4 w-1/2 bg-[#f5f1f2] rounded" />
                </div>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <HeaderPro />
        <main className="flex-1 max-w-[800px] mx-auto px-4 py-16 text-center">
          <span className="material-icons text-[48px] text-[#eee] mb-3">
            event_busy
          </span>
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[22px] mb-2">
            Booking not found
          </h1>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] mb-6">
            We couldn't find this booking. It may have been cancelled or
            re-assigned.
          </p>
          <button
            type="button"
            onClick={() => navigate('/professional/bookings')}
            className="h-[44px] px-6 rounded-[10px] bg-[#8e2a2f] hover:bg-[#7a2428] text-white font-['DM_Sans',sans-serif] font-bold text-[14px] transition-colors"
          >
            Back to bookings
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  const statusLabel = STATUS_LABEL[info.status] ?? info.status;
  const statusStyle =
    STATUS_STYLES[info.status] ?? 'bg-gray-100 text-gray-600';
  // A reschedule request shares PENDING_PROFESSIONAL_CONFIRM but is a
  // paid, already-confirmed booking the customer wants to MOVE — it gets
  // Approve/Decline-reschedule, never the fresh accept/reject (those
  // would refund a paid booking, and the server rejects them anyway).
  const isReschedulePending =
    info.status === 'PENDING_PROFESSIONAL_CONFIRM' &&
    !!info.reschedule_original_start;
  // Accept / Reject only apply while a FRESH booking is sitting awaiting
  // the pro's decision after a customer's payment landed.
  const canAcceptOrReject =
    info.status === 'PENDING_PROFESSIONAL_CONFIRM' && !isReschedulePending;
  const canStart = info.status === 'CONFIRMED';
  const canComplete = info.status === 'IN_PROGRESS';
  const canCancel =
    info.status === 'CONFIRMED' || info.status === 'IN_PROGRESS';
  // Decline only makes sense before work starts — once IN_PROGRESS
  // we expect the pro to either complete or cancel the booking.
  const canDecline = info.status === 'CONFIRMED';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <HeaderPro />

      <main className="flex-1 pb-16">
        <div className="max-w-[800px] mx-auto px-4 py-8">
          {/* Header */}
          <button
            type="button"
            onClick={() => navigate('/professional/bookings')}
            className="flex items-center gap-1 text-sm text-[#908e8e] hover:text-[#8e2a2f] mb-4"
          >
            <span className="material-icons text-[18px]">navigate_before</span>
            Back to bookings
          </button>

          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] leading-[32px] mb-2">
                {info.service?.name ?? 'Booking'}
              </h1>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
                Reference{' '}
                <span className="font-mono text-[#3a3335]">
                  {info.reference}
                </span>
              </p>
            </div>
            <span
              className={`px-4 py-2 rounded-[8px] font-['DM_Sans',sans-serif] font-bold text-[12px] whitespace-nowrap ${statusStyle}`}
            >
              {statusLabel}
            </span>
          </div>

          {/* Reschedule request banner */}
          {isReschedulePending && (
            <div className="flex items-start gap-2 mb-6 p-3 rounded-[10px] bg-[#fff7e6] border border-[#fde2a1]">
              <span className="material-icons text-[18px] text-[#9a6700] mt-[1px]">
                event_repeat
              </span>
              <p className="font-['DM_Sans',sans-serif] text-[#7a5400] text-[13px] leading-[19px]">
                The customer asked to move this booking from{' '}
                <strong>
                  {formatDateTime(info.reschedule_original_start)}
                </strong>{' '}
                to <strong>{formatDateTime(info.scheduled_start)}</strong>.
                Approve to move it, or decline to keep the original time
                (nothing is cancelled or refunded either way).
              </p>
            </div>
          )}

          {/* Actions */}
          {canAcceptOrReject ||
          isReschedulePending ||
          canStart ||
          canComplete ||
          canCancel ||
          canDecline ? (
            <div className="flex flex-wrap gap-2 mb-8">
              {isReschedulePending && (
                <>
                  <ActionButton
                    icon="event_available"
                    label="Approve new time"
                    disabled={acting}
                    onClick={() => handleAction('approveReschedule')}
                  />
                  <ActionButton
                    icon="event_busy"
                    label="Decline new time"
                    disabled={acting}
                    danger
                    onClick={() => handleAction('declineReschedule')}
                  />
                </>
              )}
              {canAcceptOrReject && (
                <>
                  <ActionButton
                    icon="check"
                    label="Accept booking"
                    disabled={acting}
                    onClick={() => handleAction('accept')}
                  />
                  <ActionButton
                    icon="close"
                    label="Reject booking"
                    disabled={acting}
                    danger
                    onClick={() => handleAction('reject')}
                  />
                </>
              )}
              {canStart && (
                <ActionButton
                  icon="play_arrow"
                  label="Mark as in progress"
                  disabled={acting}
                  onClick={() => handleAction('start')}
                />
              )}
              {canComplete && (
                <ActionButton
                  icon="check_circle"
                  label="Mark as completed"
                  disabled={acting}
                  onClick={() => handleAction('complete')}
                />
              )}
              {canDecline && (
                <ActionButton
                  icon="person_off"
                  label="Decline assignment"
                  disabled={acting}
                  onClick={() => handleAction('decline')}
                />
              )}
              {info.customer?.id ? (
                <ActionButton
                  icon="chat_bubble_outline"
                  label="Message customer"
                  onClick={() =>
                    navigate('/professional/messenger', {
                      state: { openWithUserId: info.customer.id },
                    })
                  }
                />
              ) : null}
              {canCancel && (
                <ActionButton
                  icon="event_busy"
                  label="Cancel booking"
                  disabled={acting}
                  danger
                  onClick={() => handleAction('cancel')}
                />
              )}
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card title="When">
              <KV label="Scheduled" value={formatDateTime(info.scheduled_start)} />
              <KV
                label="Duration"
                value={`${info.duration_minutes} min`}
              />
              <KV
                label="Source"
                value={info.source === 'SUBSCRIPTION' ? 'Recurring' : 'One-off'}
              />
            </Card>
            <Card title="Customer">
              <KV
                label="Name"
                value={fullName(
                  info.customer?.first_name ?? null,
                  info.customer?.last_name ?? null,
                )}
              />
              <KV label="Email" value={info.customer?.email ?? null} />
              {info.customer?.mobile_number ? (
                <KV
                  label="Phone"
                  value={`${info.customer?.dial_code ?? ''} ${info.customer?.mobile_number}`}
                />
              ) : null}
            </Card>
            <Card title="Service address">
              <p className="text-sm text-[#3a3335]">
                {info.address_line1}
                {info.address_line2 ? <>, {info.address_line2}</> : null}
              </p>
              <p className="text-sm text-[#908e8e]">
                {[info.city, info.postcode, info.country_code]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            </Card>
            <Card title="Service">
              <KV
                label="Category"
                value={info.service_category?.name ?? null}
              />
              {/* List every booked line item (base services + add-ons)
                  so the pro sees the full scope — matches the
                  customer's price breakdown. Falls back to the single
                  primary service when itemised rows aren't present. */}
              {Array.isArray(info.items) && info.items.length > 0 ? (
                info.items.map((item: any) => (
                  <KV
                    key={item.id}
                    label="Service"
                    value={
                      Number(item.quantity) > 1
                        ? `${item.name} ×${Number(item.quantity)}`
                        : item.name
                    }
                  />
                ))
              ) : (
                <KV label="Service" value={info.service?.name ?? null} />
              )}
            </Card>

            {/* Payout breakdown — backend only returns this for
                COMPLETED bookings (payouts are generated on completion
                only). For everything else we just show the gross
                figure so the pro knows what the customer paid. */}
            {info.payout_breakdown ? (
              <Card title="Your payout">
                <KV
                  label="Customer paid (gross)"
                  value={formatMoney(
                    Number(info.payout_breakdown.gross_amount),
                    info.payout_breakdown.currency ?? 'GBP',
                  )}
                />
                <KV
                  label={`Cherry Picked fee (${info.payout_breakdown.platform_fee_percentage}%)`}
                  value={`− ${formatMoney(
                    Number(info.payout_breakdown.platform_fee_amount),
                    info.payout_breakdown.currency ?? 'GBP',
                  )}`}
                />
                <div className="pt-2 mt-2 border-t border-[#ede8e9]">
                  <KV
                    label="You receive"
                    value={formatMoney(
                      Number(info.payout_breakdown.net_amount),
                      info.payout_breakdown.currency ?? 'GBP',
                    )}
                  />
                </div>
                <div className="pt-3 mt-3 border-t border-[#ede8e9]">
                  <button
                    type="button"
                    onClick={() => {
                      const customerName = fullName(
                        info.customer?.first_name ?? null,
                        info.customer?.last_name ?? null,
                      );
                      const addressLine = [
                        info.address_line1,
                        info.address_line2,
                        info.city,
                        info.postcode,
                      ]
                        .filter(Boolean)
                        .join(', ');
                      downloadProPayoutInvoice({
                        reference: info.reference,
                        scheduled_start: info.scheduled_start,
                        currency: info.payout_breakdown.currency ?? 'GBP',
                        gross_amount: Number(
                          info.payout_breakdown.gross_amount,
                        ),
                        platform_fee_percentage: Number(
                          info.payout_breakdown.platform_fee_percentage,
                        ),
                        platform_fee_amount: Number(
                          info.payout_breakdown.platform_fee_amount,
                        ),
                        net_amount: Number(info.payout_breakdown.net_amount),
                        service_name: info.service?.name ?? null,
                        category_name: info.service_category?.name ?? null,
                        customer_name: customerName === '—' ? null : customerName,
                        pro_business_name:
                          info.professional?.business_name ?? null,
                        pro_name: fullName(
                          info.professional?.user?.first_name ?? null,
                          info.professional?.user?.last_name ?? null,
                        ),
                        address: addressLine || null,
                        status: info.status,
                        is_finalised: true,
                      });
                    }}
                    className="inline-flex items-center gap-1.5 px-4 h-[36px] rounded-[8px] border border-[#ede8e9] bg-white hover:border-[#8e2a2f] hover:text-[#8e2a2f] font-['DM_Sans',sans-serif] font-bold text-[13px] text-[#3a3335] transition-colors"
                  >
                    <span className="material-icons text-[18px]">
                      file_download
                    </span>
                    Download invoice
                  </button>
                </div>
              </Card>
            ) : (
              <Card title="Total billed">
                <KV
                  label="Customer paid"
                  value={formatMoney(
                    Number(info.total_amount ?? 0),
                    info.currency ?? 'GBP',
                  )}
                />
                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] mt-2">
                  Your payout breakdown will appear here once you mark
                  the booking as completed.
                </p>
              </Card>
            )}
          </div>

          {info.customer_note ||
          (info.customer_note_images &&
            info.customer_note_images.length > 0) ? (
            <div className="mt-4">
              <Card title="Customer notes">
                {info.customer_note ? (
                  <p className="text-sm whitespace-pre-wrap text-[#3a3335]">
                    {info.customer_note}
                  </p>
                ) : null}
                {info.customer_note_images &&
                info.customer_note_images.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {info.customer_note_images.map(
                      (url: string, i: number) => (
                        <a
                          key={i}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-[88px] h-[88px] rounded-lg overflow-hidden border border-[#eee]"
                          title="Open full size"
                        >
                          <img
                            src={url}
                            alt="Customer reference"
                            className="w-full h-full object-cover"
                          />
                        </a>
                      ),
                    )}
                  </div>
                ) : null}
              </Card>
            </div>
          ) : null}

          {info.items && info.items.length > 0 ? (
            <div className="mt-4">
              <Card title="Line items">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-[#908e8e]">
                    <tr>
                      <th className="py-2 font-medium">Item</th>
                      <th className="py-2 font-medium text-right">Qty</th>
                      <th className="py-2 font-medium text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {info.items.map((item: any) => (
                      <tr key={item.id} className="border-t border-[#ede8e9]">
                        <td className="py-2 text-[#3a3335]">{item.name}</td>
                        <td className="py-2 text-right text-[#908e8e]">
                          {item.quantity}
                        </td>
                        <td className="py-2 text-right text-[#3a3335]">
                          {formatMoney(
                            Number(item.line_total),
                            item.currency ?? 'GBP',
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
          ) : null}

          {info.cancelled_at || info.refund_outcome !== 'NONE' ? (
            <div className="mt-4">
              <Card title="Cancellation & refund">
                <KV label="Cancelled" value={formatDateTime(info.cancelled_at)} />
                <KV label="By" value={info.cancelled_by ?? null} />
                {info.cancellation_reason ? (
                  <KV label="Reason" value={info.cancellation_reason} />
                ) : null}
                <KV label="Refund" value={info.refund_outcome ?? null} />
                {Number(info.refund_amount ?? 0) > 0 ? (
                  <KV
                    label="Refund amount"
                    value={formatMoney(
                      Number(info.refund_amount),
                      info.currency ?? 'GBP',
                    )}
                  />
                ) : null}
              </Card>
            </div>
          ) : null}
        </div>
      </main>

      <Footer />

      {/* Branded accept / reject / cancel confirmation. Replaces the
          native browser confirm() — both for consistency with the
          rest of the app's modals (CancelBookingModal, ReportProblem,
          etc.) and because the refund-consequences copy is too long
          to fit in a native dialog. */}
      <ConfirmModal
        isOpen={pendingConfirm !== null}
        onClose={() => {
          if (!acting) setPendingConfirm(null);
        }}
        onConfirm={runConfirmed}
        confirming={acting}
        title={pendingConfirm ? CONFIRM_COPY[pendingConfirm].title : ''}
        message={pendingConfirm ? CONFIRM_COPY[pendingConfirm].message : ''}
        confirmLabel={
          pendingConfirm ? CONFIRM_COPY[pendingConfirm].confirmLabel : 'Confirm'
        }
        cancelLabel="Keep it"
      />
    </div>
  );
}

function ActionButton({
  icon,
  label,
  onClick,
  disabled,
  danger,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-[8px] font-['DM_Sans',sans-serif] font-bold text-[13px] transition-colors disabled:opacity-50 ${
        danger
          ? 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-100 hover:bg-red-100'
          : 'bg-[#8e2a2f] text-white hover:bg-[#7a2428]'
      }`}
    >
      <span className="material-icons text-[18px]">{icon}</span>
      {label}
    </button>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-[#ede8e9] rounded-[10px] bg-white p-5">
      <h2 className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[13px] mb-3 uppercase tracking-wide">
        {title}
      </h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function KV({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-[#908e8e]">{label}</span>
      <span className="text-[#3a3335] text-right max-w-[60%] truncate">
        {value || '—'}
      </span>
    </div>
  );
}
